<template>
  <div class="c_product_header_premium__wrapper">
    <div
      class="c_product_header_premium"
      data-theme="dark"
      :style="{
        '--product-header-premium--height': data.heightOverride || null,
        '--product-header-premium--height--mobile': data.heightOverrideMobile || null,
      }"
    >
      <div class="c_product_header_premium__media_wrapper" v-if="mediaType">
        <MediaImage
          v-if="mediaType === 'images_Asset'"
          :sources="[data.media[0].uncropped1920w]"
          :alt="data.media[0].title"
        />
        <video
          v-if="displayVideo"
          loading="eager"
          source="file"
          muted
          autoplay
          playsinline=""
          loop
          ref="headerPremiumVideoRef"
          id="c_header_premium_video__ref"
          :poster="videoPoster"
        >
          <source :src="data.media?.[0]?.url" />
        </video>
      </div>
      <div class="c_product_header_premium__content_wrapper">
        <div class="c_wrapper">
          <h2 v-if="data.sectionTitle" v-html="data.sectionTitle"></h2>
          <div class="c_2022--body" v-if="data.description" v-html="data.description"></div>
          <div v-if="ctas.length || links.length" class="c_product_header_premium__links_wrapper">
            <ul v-if="ctas.length" class="c_product_header_premium__ctas">
              <!-- CTAs -->
              <li v-for="(linkData, index) in ctas" :key="`c_product_header_premium__ctas--${index}`">
                <!-- Leaving Site -->
                <Button
                  v-if="linkData.link?.url && linkData?.enableModal"
                  :class="[
                    linkData.linkStyle === 'ctaDefault'
                      ? 'c_button_2022 c_button_2022--primary'
                      : 'c_button_2022 c_button_2022__secondary',
                  ]"
                  :aria-label="linkData?.link?.ariaLabel"
                  @onClick="triggerExternalLinkModal(linkData)"
                  unstyle
                  reset
                >
                  {{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}
                </Button>

                <!-- Video (YouTube / MP4) -->
                <Button
                  v-else-if="hasVideo(linkData.link?.element) && linkData.link?.url"
                  :class="[
                    linkData.linkStyle === 'ctaDefault'
                      ? 'c_button_2022 c_button_2022--primary'
                      : 'c_button_2022 c_button_2022__secondary',
                  ]"
                  :aria-label="linkData?.link?.ariaLabel"
                  @onClick="handleVideoClick(linkData)"
                  unstyle
                  reset
                >
                  {{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}
                </Button>

                <!-- Normal link -->
                <a
                  v-else-if="linkData.link?.url"
                  :href="linkData.link?.url"
                  :target="linkData.link?.target"
                  :aria-label="linkData.link?.ariaLabel"
                  :class="[
                    linkData.linkStyle === 'ctaDefault'
                      ? 'c_button_2022 c_button_2022--primary'
                      : 'c_button_2022 c_button_2022__secondary',
                  ]"
                >
                  {{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}
                </a>
              </li>
            </ul>
            <ul v-if="links.length" class="c_product_header_premium__links">
              <!-- Links -->
              <li v-for="(linkData, index) in links" :key="`c_product_header_premium_link--${index}`">
                <!-- Leaving Site -->
                <Button
                  v-if="linkData.link?.url && linkData.enableModal"
                  class="c_product_header_premium__linkDefault"
                  :aria-label="linkData.link?.ariaLabel"
                  @onClick="triggerExternalLinkModal(linkData)"
                  unstyle
                  reset
                >
                  <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
                  <IconSVG width="14" height="14" handle="new_window" v-if="linkData.link?.target" />
                </Button>

                <!-- Video Link (YouTube / MP4) -->
                <Button
                  v-else-if="hasVideo(linkData.link?.element) && linkData.link?.url && !linkData.enableModal"
                  class="c_product_header_premium__linkDefault"
                  :aria-label="linkData.link?.ariaLabel"
                  @onClick="handleVideoClick(linkData)"
                  unstyle
                  reset
                >
                  <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
                  <IconSVG width="14" height="14" handle="new_window" v-if="linkData.link?.target" />
                </Button>

                <!-- Regular Link -->
                <a
                  v-else-if="linkData.link?.url && !linkData.enableModal"
                  :href="linkData.link?.url"
                  :target="linkData.link?.target"
                  :aria-label="linkData.link?.ariaLabel"
                  class="c_product_header_premium__linkDefault"
                >
                  <span>{{ linkData.link?.customText || linkData.link?.text || $t('Learn More') }}</span>
                  <IconSVG width="14" height="14" handle="new_window" v-if="linkData.link?.target" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <button
      class="c_product_header_premium__playback"
      aria-controls="c_header_premium_video__ref"
      @click="toggleVideo"
      :aria-label="$t('Media - Play')"
      v-if="displayVideo"
    >
      <IconSVG :handle="videoIsPaused ? 'hotspot_play' : 'hotspot_pause'" color="white" />
    </button>
    <Overlay
      class="c_product_header_premium__overlay"
      ref="videoBannerOverlay"
      theme="full"
      :show-close-button="true"
      @close="closeVideoModal"
    >
      <div class="c_box--pos--relative c_product_header_premium__overlay__video">
        <!-- YouTube -->
        <MediaVideo
          v-if="videoSource === 'youtube'"
          loading="eager"
          :source="videoSource"
          :video-id="videoId"
          controls
          autoplay
          playsinline
        />

        <!-- MP4 -->
        <video
          v-else-if="videoSource === 'mp4' && videoUrl"
          class="c_product_header_premium__overlay__video__player"
          :src="videoUrl"
          controls
          playsinline
          autoplay
        />
      </div>
    </Overlay>
    <Overlay
      class="c_external_link__overlay"
      ref="external_link_overlay"
      :show-close-button="true"
      rounded
      @overlayHide="clearExternalLinkModal"
    >
      <ExternalLinkModal
        :disclaimers="externalLinkDisclaimer"
        :url="externalLinkUrl"
        @hideExternalLinkModal="hideExternalLinkModal"
      />
    </Overlay>
  </div>
</template>

<script>
import { defineComponent, ref, reactive, toRefs } from '@nuxtjs/composition-api';
import Button from 'Components/button/Button.vue';
import ExternalLinkModal from 'Components/external_link_modal/ExternalLinkModal.vue';
import IconSVG from 'Components/icon_svg/IconSVG.vue';
import MediaImage from 'Components/image/MediaImage.vue';
import MediaVideo from 'Components/video/MediaVideo.vue';
import Overlay from 'Components/overlay/Overlay.vue';
import { getVideoPayload, hasVideo } from 'JS/global.js';

export default defineComponent({
  name: 'ProductHeaderPremium',
  components: {
    Button,
    ExternalLinkModal,
    IconSVG,
    MediaImage,
    MediaVideo,
    Overlay,
  },
  props: {
    data: { type: Object, default: () => {} },
  },
  setup(props) {
    const state = reactive({
      headerPremiumVideoRef: ref(null),
      mediaType: ref(),
      displayVideo: ref(false),
      videoIsPaused: false,
      videoBannerOverlay: ref(null),
      videoSource: ref(''),
      videoId: ref(''),
      videoUrl: ref(''),
    });

    // Separate ctas and links
    const ctas = (props.data.links || []).filter((l) => l.linkStyle !== 'linkDefault');
    const links = (props.data.links || []).filter((l) => l.linkStyle === 'linkDefault');

    state.mediaType = props.data?.media?.[0]?.__typename || null;
    state.displayVideo = state.mediaType === 'videoUploads_Asset';

    const toggleVideo = () => {
      state.videoIsPaused = !state.videoIsPaused;
      state.videoIsPaused ? state.headerPremiumVideoRef?.pause() : state.headerPremiumVideoRef?.play();
    };

    const videoPoster = props?.data.media?.[0]?.videoThumbnail?.[0]?.uncropped1920w?.srcWebp || undefined;

    // External Link Modal functionality
    const externalLinkDisclaimer = ref([]);
    const externalLinkUrl = ref('');
    const external_link_overlay = ref(null);
    const triggerExternalLinkModal = (link) => {
      if (link?.disclaimerFootnote?.[0]) {
        externalLinkDisclaimer.value.push(link.disclaimerFootnote[0]);
      }
      externalLinkUrl.value = link.link?.url || 'https://kodak.com/en/';
      if (external_link_overlay.value && external_link_overlay.value.show) {
        external_link_overlay.value.show();
      }
    };

    const openVideoModal = (el) => {
      const video = getVideoPayload(el);
      if (!video) return;

      if (video.type === 'youtube') {
        state.videoSource = video.source || 'youtube';
        state.videoId = video.id;
        state.videoUrl = null;
      } else if (video.type === 'mp4') {
        state.videoSource = 'mp4';
        state.videoId = null;
        state.videoUrl = video.url;
      }
      state.videoBannerOverlay?.show?.();
    };

    const closeVideoModal = () => {
      state.videoBannerOverlay?.hide?.();
      state.videoSource = null;
      state.videoId = null;
      state.videoUrl = null;
    };

    const clearExternalLinkModal = () => {
      externalLinkDisclaimer.value = [];
      externalLinkUrl.value = '';
    };
    const hideExternalLinkModal = () => {
      if (external_link_overlay?.value && external_link_overlay?.value?.hide) {
        external_link_overlay.value.hide();
      }
    };

    const handleVideoClick = (linkData) => {
      const el = linkData?.link?.element;
      openVideoModal(el);
    };

    return {
      ...toRefs(state),
      clearExternalLinkModal,
      closeVideoModal,
      ctas,
      external_link_overlay,
      externalLinkDisclaimer,
      externalLinkUrl,
      getVideoPayload,
      handleVideoClick,
      hasVideo,
      hideExternalLinkModal,
      links,
      openVideoModal,
      toggleVideo,
      triggerExternalLinkModal,
      videoPoster,
    };
  },
});
</script>
<style lang="scss">
.c_product_header_premium__wrapper {
  position: relative;
}

.c_product_header_premium {
  --product-header-premium--height: 600px;
  --product-header-premium--height--mobile: 600px;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: var(--product-header-premium--height--mobile);
  min-height: 600px;
  aspect-ratio: auto;
  display: flex;
  align-items: flex-end;
  color: $color_white;
  @include mq(1024) {
    height: var(--product-header-premium--height);
    // aspect-ratio: 16/9;
  }
  @media (min-width: 1080px) {
    max-height: 1080px;
  }
  &[data-theme='dark'] {
    --product-header-premium-button-bg-primary: var(--color-white);
    --product-header-premium-button-color-primary: var(--color-black);
    --product-header-premium-link-color: var(--color-white);
  }
  &:after {
    content: '';
    position: absolute;
    inset: 0;
    height: 100%;
    width: 100%;
    z-index: 5;
    user-select: none;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.27) 19.96%,
      rgba(0, 0, 0, 0.58) 41.42%,
      rgba(0, 0, 0, 0.78) 59.4%,
      rgba(0, 0, 0, 0.92) 79.23%,
      #000 94%,
      #000 100%
    );
  }
  .c_wrapper {
    position: relative;
    z-index: 15;
    padding-top: px_to_rem(64);
    padding-bottom: px_to_rem(32);
    flex-grow: 1;
    @include mq($mq_md_to_lg) {
      padding-bottom: px_to_rem(64);
    }
  }
  &__content_wrapper {
    position: relative;
    z-index: 10;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: flex-end;
    flex-grow: 1;
  }
  &__media_wrapper {
    position: absolute;
    z-index: 1;
    inset: 0;
    figure {
      height: 100%;
    }
    picture {
      display: flex;
      height: 100%;
    }
    img,
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
      background-color: $color_black;
    }
  }
  &__links_wrapper {
    display: flex;
    gap: px_to_rem(32);
    align-items: flex-start;
    margin-top: px_to_rem(42);
    flex-direction: column;
    @include mq($mq_md_to_lg) {
      flex-direction: row;
      align-items: center;
    }
  }
  &__ctas {
    display: flex;
    flex-flow: wrap;
    gap: px_to_rem(16);
    justify-content: center;
    order: 2;
    width: 100%;
    li {
      flex-grow: 1;
    }
    a {
      width: 100%;
    }
    @include mq($mq_md_to_lg) {
      width: auto;
      order: 1;
      a {
        width: auto;
      }
    }
  }
  &__links {
    display: flex;
    flex-flow: wrap;
    gap: px_to_rem(20);
    order: 1;
    a,
    button {
      color: $color_white;
      display: flex;
      align-items: center;
      gap: px_to_rem(8);
      line-height: 1;
      &:visited,
      &:active {
        color: $color_white;
      }
      @include font('graphik_semibold');
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
    @include mq($mq_md_to_lg) {
      order: 2;
    }
  }
  h2 {
    @include font('graphik_bold');
    @include fs_lh(40, 48, 40, 48);
    margin-bottom: px_to_rem(16);
    max-width: px_to_rem(700);
  }
  .c_2022--body {
    @include fs_lh(18, 28, 18, 28);
    max-width: px_to_rem(700);
  }
  .c_button_2022--primary {
    background-color: var(--product-header-premium-button-bg-primary);
    border-color: var(--product-header-premium-button-bg-primary);
    color: var(--product-header-premium-button-color-primary);
    &:hover {
      color: var(--product-header-premium-button-bg-primary);
      background-color: transparent;
    }
  }
  .c_button_2022__secondary {
    border-color: var(--product-header-premium-button-bg-primary);
    &:hover {
      background-color: var(--product-header-premium-button-bg-primary);
      color: $color_black;
    }
  }
  &__playback {
    position: absolute;
    padding: 0;
    z-index: 100;
    border-radius: 50%;
    width: px_to_rem(36);
    height: px_to_rem(36);
    background-color: #393939;
    border: 1px solid #555555;
    color: $color_white;
    display: flex;
    align-items: center;
    justify-content: center;
    top: px_to_rem(20);
    left: auto;
    bottom: auto;
    right: px_to_rem(20);
    &:hover {
      background-color: lighten(#393939, 3%);
    }
    svg {
      width: px_to_rem(10);
      height: px_to_rem(10);
    }
    @include mq($mq_md_to_lg) {
      top: auto;
      left: auto;
      bottom: px_to_rem(24);
      right: px_to_rem(24);
    }
  }
  &__overlay {
    background: rgba($color_black_raw, 0.5);

    @at-root #{&}__video {
      width: 90vw;
      max-width: 100%;
      overflow: hidden;
      @include mq($mq_sm_to_md) {
        width: 70vw;
      }
      @include mq($mq_md_to_lg) {
        width: px_to_rem(800);
      }
    }
  }
}
</style>
